import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { PasswordStrengthMeter } from "pages/auth/PasswordStrengthMeter";
import { Flex, Text } from "styles/utility-components";

import TextButton from "components/actions/TextButton";
import Alert from "components/ui/alert/Alert";
import Button from "components/ui/button/Button";
import Checkbox from "components/ui/checkbox/Checkbox";
import Input from "components/ui/input/Input";
import Label from "components/ui/input/Label";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { toast } from "components/ui/toast/Toast";
import { betterAuthErrorText } from "lib/auth/authClient";
import {
  confirmPasswordRules,
  newPasswordFieldRules,
  passwordFieldRules,
} from "lib/auth/passwordRules";
import { AUTH_SESSIONS_LIST_IN_UI, useVariableValue } from "lib/featureFlags";
import { useChangePassword } from "lib/hooks/api/auth/useChangePassword";
import { hasPasswordAccount, useListAccounts } from "lib/hooks/api/auth/useListAccounts";
import { useRequestPasswordReset } from "lib/hooks/api/auth/useRequestPasswordReset";
import { useCompanyAuthConfig } from "lib/hooks/api/company/useCompanyAuthConfig";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { FieldContainer } from "./utils";

export const RESEND_COOLDOWN_SECONDS = 60;

const INCORRECT_CURRENT_PASSWORD = "Current password is incorrect";
const SAME_AS_CURRENT_PASSWORD = "Your new password must be different from your current one";

// Refusals from auth-service when the company doesn't allow password sign-in.
const PASSWORD_NOT_ALLOWED_ERRORS: Record<string, string> = {
  SSO_REQUIRED:
    "Your company signs in with single sign-on (SSO), so you can't use a password with Stotles.",
  LOGIN_METHOD_NOT_ALLOWED: "Your company doesn't allow signing in with a password.",
};

type ChangePasswordFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

function ChangePasswordForm({ onForgotPassword }: { onForgotPassword: () => void }) {
  // Other devices stay signed in through the app's own session cookie, so only offer signing them
  // out where that's been dealt with.
  const canRevokeOtherSessions = useVariableValue(AUTH_SESSIONS_LIST_IN_UI, false);
  const [revokeOtherSessions, setRevokeOtherSessions] = useState(false);
  const [formError, setFormError] = useState<string>();
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    trigger,
    reset,
    setError,
    formState: { isValid, errors },
  } = useForm<ChangePasswordFormValues>({
    // Errors wait until a field is left, so they don't flash while typing; isValid stays live.
    mode: "onTouched",
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // The new password must differ from the current one, so re-check it as the current one changes,
  // but only once it's been entered (an empty new password would otherwise show "required").
  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name === "currentPassword" && values.newPassword) void trigger("newPassword");
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  const { mutate: changePassword, isLoading } = useChangePassword({
    onSuccess: () => {
      reset();
      setRevokeOtherSessions(false);
      toast.success("Your password has been changed");
    },
    onError: (error) => {
      if (error.code === "INVALID_PASSWORD") {
        setError("currentPassword", { message: INCORRECT_CURRENT_PASSWORD });
        return;
      }
      setFormError(
        (error.code && PASSWORD_NOT_ALLOWED_ERRORS[error.code]) ||
          "We couldn't change your password. Please try again, or contact us if the problem persists.",
      );
    },
  });

  const onSubmit = ({ currentPassword, newPassword }: ChangePasswordFormValues) => {
    setFormError(undefined);
    changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: canRevokeOtherSessions && revokeOtherSessions,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex column gap={16}>
        <FieldContainer>
          <Label htmlFor="currentPassword">Current password</Label>
          <Input
            name="currentPassword"
            control={control}
            type="password"
            autoComplete="current-password"
            rules={passwordFieldRules}
          />
          {errors.currentPassword?.message && (
            <Text description color="sysDestructiveDefault">
              {errors.currentPassword.message}
            </Text>
          )}
          <div>
            <TextButton onClick={onForgotPassword}>Forgot your current password?</TextButton>
          </div>
        </FieldContainer>
        <FieldContainer>
          <Label htmlFor="newPassword">New password</Label>
          <Input
            name="newPassword"
            control={control}
            type="password"
            autoComplete="new-password"
            rules={{
              ...newPasswordFieldRules,
              validate: {
                ...newPasswordFieldRules.validate,
                differsFromCurrent: (value: string) =>
                  !value || value !== getValues("currentPassword") || SAME_AS_CURRENT_PASSWORD,
              },
              deps: ["confirmPassword"],
            }}
          />
          {errors.newPassword?.message && (
            <Text description color="sysDestructiveDefault">
              {errors.newPassword.message}
            </Text>
          )}
          <PasswordStrengthMeter password={watch("newPassword")} />
        </FieldContainer>
        <FieldContainer>
          <Label htmlFor="confirmPassword">Confirm new password</Label>
          <Input
            name="confirmPassword"
            control={control}
            type="password"
            autoComplete="new-password"
            rules={confirmPasswordRules(() => getValues("newPassword"))}
          />
          {errors.confirmPassword?.message && (
            <Text description color="sysDestructiveDefault">
              {errors.confirmPassword.message}
            </Text>
          )}
        </FieldContainer>
        {canRevokeOtherSessions && (
          <Flex gap={8} alignItems="center">
            <Checkbox
              id="revokeOtherSessions"
              checked={revokeOtherSessions}
              onCheckedChange={(checked) => setRevokeOtherSessions(checked === true)}
            />
            <Label htmlFor="revokeOtherSessions">Sign out of other devices</Label>
          </Flex>
        )}
        {formError && <Alert type="error">{formError}</Alert>}
        <div>
          <Button buttonType="primary" type="submit" loading={isLoading} disabled={!isValid}>
            Change password
          </Button>
        </div>
      </Flex>
    </form>
  );
}

/** Emails the user a link to set a password, for when they have none or have forgotten it. */
function PasswordEmail({ hasPassword }: { hasPassword: boolean }) {
  const currentUser = useAssertCurrentUser();
  const { mutate: requestPasswordReset, isLoading, error } = useRequestPasswordReset();
  // Latched, so a resend in flight keeps showing that an email has already gone out.
  const [hasSentPasswordResetEmail, setHasSentPasswordResetEmail] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const send = () =>
    requestPasswordReset(
      { email: currentUser.email },
      {
        onSuccess: () => {
          setHasSentPasswordResetEmail(true);
          setCooldown(RESEND_COOLDOWN_SECONDS);
        },
      },
    );

  let buttonLabel = hasPassword ? "Send reset link" : "Set a password";
  if (hasSentPasswordResetEmail) {
    buttonLabel = cooldown > 0 ? `Resend email in ${cooldown}s` : "Resend email";
  }

  return (
    <Flex column gap={16} alignItems="flex-start">
      {hasSentPasswordResetEmail ? (
        <Alert type="info">
          We've sent an email to <strong>{currentUser.email}</strong>. Follow the link in it to{" "}
          {hasPassword ? "reset" : "set"} your password.
        </Alert>
      ) : (
        <Text>
          {hasPassword
            ? "We'll email you a link to reset your password."
            : "You sign in without a password at the moment. To add one, we'll email you a link to set it."}{" "}
          It will be sent to <strong>{currentUser.email}</strong>.
        </Text>
      )}
      {!!error && (
        <Alert type="error">
          {betterAuthErrorText(error, "We couldn't send the email. Please try again later.")}
        </Alert>
      )}
      <Button onClick={send} loading={isLoading} disabled={cooldown > 0}>
        {buttonLabel}
      </Button>
    </Flex>
  );
}

/** Change the user's password, or get one if they don't have it or their company allows it. */
function ChangePassword(): JSX.Element {
  const {
    data: accounts,
    isLoading: isLoadingAccounts,
    isError: isAccountsError,
  } = useListAccounts();
  const {
    data: authConfig,
    isLoading: isLoadingAuthConfig,
    isError: isAuthConfigError,
  } = useCompanyAuthConfig();
  const [forgotPassword, setForgotPassword] = useState(false);

  if (isLoadingAccounts || isLoadingAuthConfig) {
    return <SkeletonText width="320px" />;
  }

  // Without both, we can't tell whether the user has a password or may use one.
  if (isAccountsError || isAuthConfigError) {
    return (
      <Text color="sysDestructiveDefault">
        We couldn't load your password settings. Please refresh the page to try again.
      </Text>
    );
  }

  if (authConfig && !authConfig.allowPasswordLogin) {
    return (
      <Text color="sysTextSecondary">
        Your company manages how you sign in to Stotles, and doesn't allow passwords. Sign in with
        your company's single sign-on (SSO) or another method it allows.
      </Text>
    );
  }

  const hasPassword = hasPasswordAccount(accounts);
  if (hasPassword && !forgotPassword) {
    return <ChangePasswordForm onForgotPassword={() => setForgotPassword(true)} />;
  }
  return <PasswordEmail hasPassword={hasPassword} />;
}

export default ChangePassword;
